/**
 * Shift Dialog Component
 * 
 * Dialog for creating and editing shift assignments.
 * Extracted from Shifts.jsx for modularity.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { User, Loader2, Trash2, MapPin, Info, Repeat, AlertTriangle, CheckCircle2, UserCog, Sparkles, CalendarDays, Briefcase } from 'lucide-react';
import { shiftsAPI, honorarkraefteAPI, roomsAPI } from '../../lib/api';
import api from '../../lib/api';
import { resolveMassnahmeTimesForDate } from './massnahmeZeit';
import { Switch } from '../../components/ui/switch';
import { MultiDayPicker } from '../../components/MultiDayPicker';

const formatDateLabel = (iso) => {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
};
import { DatePickerDE } from '../../components/ui/date-picker-de';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { formatLastFirst, getDuplicateNameKeys } from '../../lib/nameFormat';

const ShiftDialog = ({
  open,
  onOpenChange,
  selectedShift,
  formData,
  setFormData,
  massnahmen,
  employees,
  locations,
  rooms,
  saving,
  onSave,
  onDelete,
  onDeleteSeries,
  onReassignSeries,
  selectedLocationId,  // Current filter location
  recurrence = { mode: 'once', end_date: '' },
  setRecurrence,
  // Mehrtages-Modus (nur beim Anlegen, nicht beim Bearbeiten)
  multiDayDays = [],
  setMultiDayDays,
}) => {
  const multiDayEnabled = multiDayDays.length > 0;
  // Get selected massnahme's location
  const selectedMassnahme = useMemo(() => {
    return massnahmen.find(m => m.id === formData.massnahme_id);
  }, [massnahmen, formData.massnahme_id]);

  // Maßnahmen-Zeit für den gewählten Tag (Wochentag-spezifisch, z. B. Fr 15:00)
  const massnahmeTimes = useMemo(
    () => resolveMassnahmeTimesForDate(selectedMassnahme, formData.date),
    [selectedMassnahme, formData.date],
  );

  const massnahmeLocationId = selectedMassnahme?.location_id;

  // Filter rooms based on massnahme's location
  const filteredRooms = useMemo(() => {
    if (!massnahmeLocationId) return rooms;
    return rooms.filter(r => r.location_id === massnahmeLocationId);
  }, [rooms, massnahmeLocationId]);

  // Vorab-Verfügbarkeit je Raum (booked = Raumbuchung → nicht wählbar,
  // shift = Einsatz → nur Hinweis, free = frei).
  const [roomAvailability, setRoomAvailability] = useState({});
  useEffect(() => {
    if (!open || !formData.date || filteredRooms.length === 0) {
      setRoomAvailability({});
      return;
    }
    let cancelled = false;
    roomsAPI.availability({
      date: formData.date,
      start_time: formData.start_time || undefined,
      end_time: formData.end_time || undefined,
      location_id: massnahmeLocationId || undefined,
      exclude_shift_id: selectedShift?.id || undefined,
    }).then((res) => {
      if (!cancelled) setRoomAvailability(res.data || {});
    }).catch(() => {
      if (!cancelled) setRoomAvailability({});
    });
    return () => { cancelled = true; };
  }, [open, formData.date, formData.start_time, formData.end_time, massnahmeLocationId, selectedShift, filteredRooms.length]);


  // Filter employees based on massnahme's location
  // Show employees who have this location as their main location OR in additional_location_ids
  const filteredEmployees = useMemo(() => {
    if (!massnahmeLocationId) {
      // No massnahme selected - show all employees
      return employees;
    }
    
    return employees.filter(emp => {
      // Check main location
      if (emp.location_id === massnahmeLocationId) {
        return true;
      }
      // Check additional locations
      if (emp.additional_location_ids && Array.isArray(emp.additional_location_ids)) {
        return emp.additional_location_ids.includes(massnahmeLocationId);
      }
      return false;
    });
  }, [employees, massnahmeLocationId]);

  // Honorarkräfte laden (einmalig beim Öffnen) – können ebenfalls in Schichten
  // eingesetzt werden. Filterung nach Standort der Maßnahme über location_ids.
  const [honorarkraefte, setHonorarkraefte] = useState([]);
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    honorarkraefteAPI.getAll({ is_active: true })
      .then(({ data }) => { if (!cancelled) setHonorarkraefte(data || []); })
      .catch(() => { if (!cancelled) setHonorarkraefte([]); });
    return () => { cancelled = true; };
  }, [open]);

  const filteredHonorarkraefte = useMemo(() => {
    if (!massnahmeLocationId) return honorarkraefte;
    const matching = honorarkraefte.filter(
      (h) => Array.isArray(h.location_ids) && h.location_ids.includes(massnahmeLocationId)
    );
    // Falls keine Honorarkraft dem Standort zugeordnet ist, alle anzeigen
    return matching.length > 0 ? matching : honorarkraefte;
  }, [honorarkraefte, massnahmeLocationId]);

  const isHonorar = formData.person_type === 'honorarkraft';
  const empDupKeys = useMemo(() => getDuplicateNameKeys(employees), [employees]);

  // Get location name for display
  const massnahmeLocationName = useMemo(() => {
    if (!massnahmeLocationId) return null;
    const loc = locations.find(l => l.id === massnahmeLocationId);
    return loc?.name;
  }, [locations, massnahmeLocationId]);

  // Handle massnahme change - also update location_id
  // (Zeiten werden NICHT mehr automatisch vorbefuellt - nur bei expliziter Eingabe)
  const handleMassnahmeChange = (massnahmeId) => {
    const newMassnahme = massnahmen.find(m => m.id === massnahmeId);
    // Use massnahme location, or fall back to currently selected filter location
    const newLocationId = newMassnahme?.location_id || (selectedLocationId !== 'all' ? selectedLocationId : '');

    setFormData({
      ...formData,
      massnahme_id: massnahmeId,
      employee_id: '',
      honorarkraft_id: '',
      room_id: '',
      location_id: newLocationId,
    });
  };

  // Indicator: zeigen die aktuellen Zeiten 1:1 die der Maßnahme (für den Tag)?
  const timesMatchMassnahme = Boolean(
    massnahmeTimes.start &&
    massnahmeTimes.end &&
    formData.start_time === massnahmeTimes.start &&
    formData.end_time === massnahmeTimes.end
  );

  // --- Serien-Vorschau ---
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewTimer = useRef(null);

  // --- Feiertags-Check fuer den gewaehlten Tag(zeitraum) ---
  // Zeigt eine dezente Warnung, sobald ein Einsatz auf einen Feiertag faellt.
  // Hilft Standortleiter:innen, Zuschlagspflichten frueh zu erkennen.
  const [holidaysInRange, setHolidaysInRange] = useState([]);
  const effectiveLocationId = massnahmeLocationId || formData.location_id || (selectedLocationId !== 'all' ? selectedLocationId : '');

  const isSeries = !selectedShift && recurrence?.mode && recurrence.mode !== 'once';
  const seriesReady = Boolean(
    isSeries &&
    (formData.person_type === 'honorarkraft' ? formData.honorarkraft_id : formData.employee_id) &&
    formData.massnahme_id &&
    formData.date &&
    recurrence?.end_date
    // start_time/end_time sind seit 2026-05-08 optional — Serien lassen sich
    // auch ohne feste Uhrzeiten anlegen.
  );

  useEffect(() => {
    if (!open || !formData.date || !effectiveLocationId) {
      setHolidaysInRange([]);
      return undefined;
    }
    const start = formData.date;
    const end = (isSeries && recurrence?.end_date && recurrence.end_date >= start)
      ? recurrence.end_date
      : start;
    let cancelled = false;
    api.get('/holidays-by-location', {
      params: { location_id: effectiveLocationId, start_date: start, end_date: end },
    })
      .then(({ data }) => {
        if (cancelled) return;
        setHolidaysInRange(data?.holidays || []);
      })
      .catch(() => {
        if (!cancelled) setHolidaysInRange([]);
      });
    return () => { cancelled = true; };
  }, [open, formData.date, effectiveLocationId, isSeries, recurrence?.end_date]);

  const exactDayHoliday = holidaysInRange.find((h) => h.date === formData.date);

  useEffect(() => {
    if (!seriesReady) {
      setPreview(null);
      return undefined;
    }
    if (previewTimer.current) clearTimeout(previewTimer.current);
    previewTimer.current = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await shiftsAPI.previewSeries({
          employee_id: formData.person_type === 'honorarkraft' ? null : formData.employee_id,
          honorarkraft_id: formData.person_type === 'honorarkraft' ? formData.honorarkraft_id : null,
          massnahme_id: formData.massnahme_id,
          location_id: formData.location_id || null,
          room_id: formData.room_id || null,
          start_date: formData.date,
          end_date: recurrence.end_date,
          start_time: formData.start_time,
          end_time: formData.end_time,
          pattern: recurrence.mode,
        });
        setPreview(res.data);
      } catch {
        setPreview(null);
      } finally {
        setPreviewLoading(false);
      }
    }, 350);
    return () => previewTimer.current && clearTimeout(previewTimer.current);
  }, [
    seriesReady,
    formData.employee_id,
    formData.honorarkraft_id,
    formData.person_type,
    formData.massnahme_id,
    formData.location_id,
    formData.room_id,
    formData.date,
    formData.start_time,
    formData.end_time,
    recurrence?.end_date,
    recurrence?.mode,
  ]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg w-[calc(100vw-1rem)] sm:w-full p-4 sm:p-6 max-h-[92vh] overflow-y-auto rounded-lg sm:rounded-lg"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>
            {selectedShift ? 'Einsatz bearbeiten' : 'Neuer Einsatz'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2 sm:pt-4">
          <div className="space-y-2">
            <Label>Maßnahme *</Label>
            <Select 
              value={formData.massnahme_id} 
              onValueChange={handleMassnahmeChange}
            >
              <SelectTrigger>
                <SelectValue placeholder="Maßnahme wählen..." />
              </SelectTrigger>
              <SelectContent>
                {massnahmen.map(m => (
                  <SelectItem key={m.id} value={m.id}>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: m.color }} />
                      {m.short_code} - {m.name}
                      {m.location_name && (
                        <span className="text-muted-foreground text-xs">@ {m.location_name}</span>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {(massnahmeLocationName || (formData.location_id && !massnahmeLocationId)) && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/50 rounded-md px-3 py-2">
              <MapPin className="h-4 w-4" />
              <span>Standort: <strong>
                {massnahmeLocationName || locations.find(l => l.id === formData.location_id)?.name || 'Wird aus Filter übernommen'}
              </strong></span>
              <span className="ml-auto text-xs">({filteredEmployees.length} Mitarbeiter verfügbar)</span>
            </div>
          )}

          <div className="space-y-2">
            <Label>Wer wird eingesetzt? *</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, person_type: 'mitarbeiter', honorarkraft_id: '', position_override: '' })}
                className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${!isHonorar ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border text-muted-foreground hover:bg-muted'}`}
                data-testid="shift-persontype-mitarbeiter"
              >
                <User className="h-4 w-4" /> Mitarbeiter
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, person_type: 'honorarkraft', employee_id: '', position_override: '' })}
                className={`flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors ${isHonorar ? 'border-primary bg-primary/10 text-primary font-medium' : 'border-border text-muted-foreground hover:bg-muted'}`}
                data-testid="shift-persontype-honorarkraft"
              >
                <Briefcase className="h-4 w-4" /> Honorarkraft
              </button>
            </div>
          </div>

          {!isHonorar ? (
            <div className="space-y-2">
              <Label>Mitarbeiter *</Label>
              <Select 
                value={formData.employee_id} 
                onValueChange={(v) => setFormData({ ...formData, employee_id: v })}
                disabled={!formData.massnahme_id}
              >
                <SelectTrigger data-testid="shift-employee-select">
                  <SelectValue placeholder={formData.massnahme_id ? "Mitarbeiter wählen..." : "Erst Maßnahme wählen..."} />
                </SelectTrigger>
                <SelectContent>
                  {filteredEmployees.length === 0 ? (
                    <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                      <Info className="h-4 w-4 mx-auto mb-2" />
                      Keine Mitarbeiter für diesen Standort verfügbar
                    </div>
                  ) : (
                    filteredEmployees.map(emp => {
                      const isMainLocation = emp.location_id === massnahmeLocationId;
                      const isAdditionalLocation = emp.additional_location_ids?.includes(massnahmeLocationId);
                      return (
                        <SelectItem key={emp.id} value={emp.id}>
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-muted-foreground" />
                            {formatLastFirst(emp, empDupKeys)}
                            {emp.position && <span className="text-muted-foreground">({emp.position})</span>}
                            {isAdditionalLocation && !isMainLocation && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">Zusatz</span>
                            )}
                          </div>
                        </SelectItem>
                      );
                    })
                  )}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Honorarkraft *</Label>
              <Select
                value={formData.honorarkraft_id}
                onValueChange={(v) => setFormData({ ...formData, honorarkraft_id: v })}
                disabled={!formData.massnahme_id}
              >
                <SelectTrigger data-testid="shift-honorarkraft-select">
                  <SelectValue placeholder={formData.massnahme_id ? "Honorarkraft wählen..." : "Erst Maßnahme wählen..."} />
                </SelectTrigger>
                <SelectContent>
                  {filteredHonorarkraefte.length === 0 ? (
                    <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                      <Info className="h-4 w-4 mx-auto mb-2" />
                      Keine Honorarkräfte verfügbar
                    </div>
                  ) : (
                    filteredHonorarkraefte.map(hk => (
                      <SelectItem key={hk.id} value={hk.id}>
                        <div className="flex items-center gap-2">
                          <Briefcase className="h-4 w-4 text-muted-foreground" />
                          {hk.last_name}, {hk.first_name}
                          {hk.firma_name && <span className="text-muted-foreground">({hk.firma_name})</span>}
                          {(hk.positions || []).length > 0 && !hk.firma_name && (
                            <span className="text-muted-foreground">({hk.positions[0]})</span>
                          )}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* „Tätig als"-Auswahl — nur sichtbar, wenn der gewählte Mitarbeiter
              mehrere Funktionen hinterlegt hat (position_list). Standard:
              Hauptfunktion (position). Pro Schicht kann eine andere Funktion
              gewählt werden — der Export zeigt dann die schichtspezifische. */}
          {(() => {
            const selEmp = filteredEmployees.find((e) => e.id === formData.employee_id);
            const positions = (selEmp?.position_list || []).filter(Boolean);
            if (!selEmp || positions.length <= 1) return null;
            const current = formData.position_override || selEmp.position || positions[0];
            return (
              <div className="space-y-2" data-testid="shift-position-override-section">
                <Label>Tätig als</Label>
                <Select
                  value={current}
                  onValueChange={(v) => setFormData({ ...formData, position_override: v })}
                >
                  <SelectTrigger data-testid="shift-position-override-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {positions.map((p) => (
                      <SelectItem key={p} value={p} data-testid={`shift-position-option-${p}`}>
                        <div className="flex items-center gap-2">
                          {p}
                          {p === selEmp.position && (
                            <span className="text-[10px] text-muted-foreground">★ Hauptfunktion</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Mitarbeiter hat mehrere Funktionen — wähle, in welcher Funktion {selEmp.first_name} an diesem Tag eingesetzt wird.
                </p>
              </div>
            );
          })()}

          <div className="space-y-2">
            <Label>Datum *</Label>
            <DatePickerDE
              value={formData.date}
              onChange={(v) => setFormData({ ...formData, date: v })}
              placeholder="Datum"
            />
            {exactDayHoliday && (
              <div
                className="flex items-start gap-2 rounded-md border border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20 px-3 py-2 text-xs text-purple-900 dark:text-purple-100"
                data-testid="shift-holiday-warning"
              >
                <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <div>
                  <span className="font-semibold">Feiertag:</span> {exactDayHoliday.name}
                  <div className="text-[11px] text-purple-700 dark:text-purple-300 mt-0.5">
                    Hinweis: Einsatz an einem Feiertag — ggf. zuschlagspflichtig.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* „Zeiten aus Maßnahme übernehmen"-Schnellbutton. Erscheint nur,
              wenn die Maßnahme Standard-Zeiten hat und die aktuellen Felder
              davon abweichen. Felder bleiben optional — das hier ist eine
              reine Zeitersparnis für Standort-Leiter:innen, die oft typische
              Maßnahmen-Zeiten als Ausgangspunkt nutzen. */}
          {massnahmeTimes.start && massnahmeTimes.end && !timesMatchMassnahme && (
            <div className="flex items-center justify-between gap-2 rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-2 text-xs">
              <span className="text-muted-foreground">
                Maßnahme „{selectedMassnahme.name}" hat Standard-Zeiten:{' '}
                <span className="font-medium text-foreground">
                  {massnahmeTimes.start} – {massnahmeTimes.end}
                </span>
              </span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs whitespace-nowrap"
                onClick={() => setFormData({
                  ...formData,
                  start_time: massnahmeTimes.start,
                  end_time: massnahmeTimes.end,
                })}
                data-testid="shift-apply-massnahme-times"
              >
                Übernehmen
              </Button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Von <span className="text-[10px] font-normal text-muted-foreground">(optional)</span>
                {timesMatchMassnahme && (
                  <span className="text-[10px] font-normal text-muted-foreground">(aus Maßnahme)</span>
                )}
              </Label>
              <Input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                data-testid="shift-start-time"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                Bis <span className="text-[10px] font-normal text-muted-foreground">(optional)</span>
                {timesMatchMassnahme && (
                  <span className="text-[10px] font-normal text-muted-foreground">(aus Maßnahme)</span>
                )}
              </Label>
              <Input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                data-testid="shift-end-time"
              />
            </div>
          </div>

          {isSeries && !formData.start_time && !formData.end_time && (
            <div
              className="flex items-start gap-2 rounded-md border border-dashed border-primary/40 bg-primary/5 px-3 py-2 text-xs text-muted-foreground"
              data-testid="series-autotime-hint"
            >
              <Info className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
              <span>
                Keine Zeiten angegeben — für jeden Tag der Serie wird automatisch die
                hinterlegte Maßnahmen-Zeit übernommen (z. B. freitags abweichende Endzeit).
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div className="space-y-2">
              <Label>Standort {massnahmeLocationId ? '' : '(optional)'}</Label>
              {massnahmeLocationId ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5 h-10 px-3 rounded-md border bg-muted/30 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="truncate">{massnahmeLocationName}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground">Von der Maßnahme übernommen</p>
                </div>
              ) : (
                <Select 
                  value={formData.location_id || 'none'} 
                  onValueChange={(v) => setFormData({ ...formData, location_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Keiner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Keiner</SelectItem>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label>Raum (optional)</Label>
              <Select 
                value={formData.room_id || 'none'} 
                onValueChange={(v) => setFormData({ ...formData, room_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Keiner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Keiner</SelectItem>
                  {filteredRooms.map(room => {
                    const av = roomAvailability[room.id];
                    const isBooked = av?.status === 'booked';
                    const isShift = av?.status === 'shift';
                    return (
                      <SelectItem
                        key={room.id}
                        value={room.id}
                        disabled={isBooked}
                        data-testid={`shift-room-option-${room.id}`}
                        title={av?.detail || undefined}
                      >
                        <span className="flex items-center gap-2">
                          <span>{room.name}</span>
                          {isBooked && (
                            <span className="text-xs text-destructive font-medium">• belegt (Raumbuchung)</span>
                          )}
                          {isShift && (
                            <span className="text-xs text-amber-600 font-medium">• Einsatz belegt</span>
                          )}
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {(() => {
                const cur = formData.room_id && roomAvailability[formData.room_id];
                if (cur?.status === 'shift') {
                  return <p className="text-xs text-amber-600 mt-1" data-testid="shift-room-hint">Hinweis: {cur.detail}</p>;
                }
                if (cur?.status === 'booked') {
                  return <p className="text-xs text-destructive mt-1" data-testid="shift-room-hint">{cur.detail}</p>;
                }
                return null;
              })()}
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notizen</Label>
            <Textarea 
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Optionale Anmerkungen..."
              rows={2}
            />
          </div>

          {/* Mehrtages-Auswahl — nur beim Anlegen */}
          {!selectedShift && setMultiDayDays && (
            <div className="border-t pt-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-2">
                  <CalendarDays className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <div>
                    <Label className="text-sm font-medium">Mehrere Tage auswählen</Label>
                    <p className="text-xs text-muted-foreground">
                      Wähle gezielt einzelne Tage – Einsätze werden für alle ausgewählten Tage angelegt.
                    </p>
                  </div>
                </div>
                <Switch
                  checked={multiDayEnabled}
                  onCheckedChange={(v) => {
                    if (v) {
                      // Aktivieren: aktuellen Datum als ersten Tag setzen, falls vorhanden
                      setMultiDayDays(formData.date ? [formData.date] : []);
                    } else {
                      setMultiDayDays([]);
                    }
                  }}
                  data-testid="shift-multiday-toggle"
                />
              </div>
              {multiDayEnabled && (
                <MultiDayPicker
                  selectedDays={multiDayDays}
                  onChange={setMultiDayDays}
                  weekStart={formData.date ? new Date(formData.date + 'T00:00:00') : new Date()}
                />
              )}
            </div>
          )}

          {/* Wiederholung — nur beim Neu-Anlegen und wenn nicht Mehrtages-Modus aktiv */}
          {!selectedShift && setRecurrence && !multiDayEnabled && (
            <div className="border-t pt-4">
              <Label className="text-sm font-medium mb-3 flex items-center gap-2">
                <Repeat className="h-4 w-4" />
                Wiederholung
              </Label>
              <RadioGroup
                value={recurrence.mode}
                onValueChange={(m) => setRecurrence({ ...recurrence, mode: m })}
                className="space-y-2"
              >
                {[
                  { v: 'once', label: 'Einmalig', desc: 'Nur am gewählten Datum' },
                  { v: 'daily', label: 'Täglich', desc: 'Jeden Tag bis Enddatum' },
                  { v: 'weekdays', label: 'Mo–Fr', desc: 'Werktage bis Enddatum' },
                  { v: 'weekly', label: 'Wöchentlich', desc: 'Jede Woche am gleichen Wochentag' },
                ].map(({ v, label, desc }) => (
                  <label
                    key={v}
                    htmlFor={`rec-${v}`}
                    onClick={() => setRecurrence({ ...recurrence, mode: v })}
                    className="flex items-start gap-2 text-left w-full hover:bg-muted/40 rounded-md p-2 transition-colors cursor-pointer"
                    data-testid={`recurrence-${v}`}
                  >
                    <RadioGroupItem value={v} id={`rec-${v}`} className="mt-0.5" />
                    <div className="flex-1">
                      <div className="text-sm font-medium">{label}</div>
                      <p className="text-xs text-muted-foreground">{desc}</p>
                    </div>
                  </label>
                ))}
              </RadioGroup>

              {recurrence.mode !== 'once' && (
                <div className="mt-3 space-y-2">
                  <Label className="text-xs text-muted-foreground">Enddatum *</Label>
                  <DatePickerDE
                    value={recurrence.end_date}
                    onChange={(v) => setRecurrence({ ...recurrence, end_date: v })}
                    placeholder="Letzter Tag der Serie"
                  />
                  <p className="text-xs text-muted-foreground">
                    Einsätze werden auch erstellt, wenn am Tag schon einer besteht — dieser wird dann übersprungen.
                  </p>

                  {/* Serien-Vorschau */}
                  {seriesReady && (
                    <div
                      className="mt-3 rounded-lg border border-border/60 bg-secondary/20 p-3 space-y-2"
                      data-testid="series-preview"
                    >
                      {previewLoading ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Vorschau wird berechnet …
                        </div>
                      ) : preview ? (
                        <>
                          <div className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <div className="font-medium" data-testid="series-preview-summary">
                                {preview.will_create} Einsätze werden erstellt
                                {preview.will_skip > 0 && (
                                  <span className="ml-1 text-muted-foreground font-normal">
                                    · {preview.will_skip} übersprungen (bereits in dieser Maßnahme)
                                  </span>
                                )}
                              </div>
                              {preview.will_holiday > 0 && (
                                <p className="text-xs text-rose-600 dark:text-rose-400">
                                  {preview.will_holiday} Feiertag(e) im Zeitraum – an diesen Tagen wird kein Einsatz erstellt.
                                </p>
                              )}
                              {preview.will_closure > 0 && (
                                <p className="text-xs text-rose-600 dark:text-rose-400">
                                  {preview.will_closure} Schließtag(e) im Zeitraum – an diesen Tagen wird kein Einsatz erstellt.
                                </p>
                              )}
                              {preview.will_conflict > 0 && (
                                <p className="text-xs text-amber-700 dark:text-amber-300">
                                  Hinweis: {preview.will_conflict} davon am selben Tag in anderer Maßnahme – werden angelegt und als Konflikt markiert.
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground">
                                Zeitraum insgesamt: {preview.total_candidates} Tag(e)
                              </p>
                            </div>
                          </div>

                          {holidaysInRange.length > 0 && (
                            <div
                              className="flex items-start gap-2 rounded border border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20 px-2 py-1.5 text-[11px] text-purple-900 dark:text-purple-100"
                              data-testid="series-holiday-warning"
                            >
                              <Sparkles className="h-3 w-3 mt-0.5 shrink-0" />
                              <div>
                                <span className="font-semibold">
                                  {holidaysInRange.length} {holidaysInRange.length === 1 ? 'Feiertag' : 'Feiertage'} im Zeitraum
                                </span>
                                {' · '}
                                {holidaysInRange.slice(0, 3).map((h) => h.name).join(', ')}
                                {holidaysInRange.length > 3 && ` +${holidaysInRange.length - 3}`}
                                <div className="text-[10px] text-purple-700 dark:text-purple-300 mt-0.5">
                                  Hinweis: Einsätze an Feiertagen können zuschlagspflichtig sein.
                                </div>
                              </div>
                            </div>
                          )}
                          {preview.items?.length > 0 && (
                            <div className="max-h-32 overflow-y-auto rounded border border-border/40 bg-background/60 divide-y divide-border/30">
                              {preview.items.slice(0, 50).map((it) => (
                                <div
                                  key={it.date}
                                  className={`flex items-center justify-between px-2 py-1 text-xs ${
                                    it.is_holiday || it.is_closure ? 'text-rose-600 dark:text-rose-400' : it.is_duplicate ? 'text-muted-foreground' : it.has_conflict ? 'text-amber-700 dark:text-amber-300' : ''
                                  }`}
                                  data-testid={`series-preview-${it.date}`}
                                >
                                  <span className="font-mono">
                                    {formatDateLabel(it.date)}
                                    {!it.is_holiday && !it.is_closure && !it.is_duplicate && (
                                      <span className="ml-2 text-[11px] text-muted-foreground" data-testid={`series-preview-time-${it.date}`}>
                                        {it.start_time && it.end_time ? `${it.start_time}–${it.end_time}` : 'Ganztags'}
                                      </span>
                                    )}
                                  </span>
                                  {it.is_holiday ? (
                                    <span className="inline-flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      Feiertag{it.holiday_name ? `: ${it.holiday_name}` : ''} – kein Einsatz
                                    </span>
                                  ) : it.is_closure ? (
                                    <span className="inline-flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      Schließtag{it.closure_name ? `: ${it.closure_name}` : ''} – kein Einsatz
                                    </span>
                                  ) : it.is_duplicate ? (
                                    <span className="inline-flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      übersprungen (Duplikat)
                                    </span>
                                  ) : it.has_conflict ? (
                                    <span className="inline-flex items-center gap-1">
                                      <AlertTriangle className="h-3 w-3" />
                                      wird erstellt – Konflikt{it.conflict_with ? `: ${it.conflict_with}` : ''}
                                    </span>
                                  ) : (
                                    <span className="text-emerald-600 dark:text-emerald-400">wird erstellt</span>
                                  )}
                                </div>
                              ))}
                              {preview.items.length > 50 && (
                                <div className="px-2 py-1 text-[11px] text-muted-foreground italic text-center">
                                  … und {preview.items.length - 50} weitere
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      ) : null}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-2 pt-4 border-t">
            <div className="flex flex-col sm:flex-row gap-2 sm:flex-wrap">
              {selectedShift && (
                <Button
                  variant="destructive"
                  onClick={onDelete}
                  disabled={saving}
                  data-testid="delete-shift-btn"
                  className="w-full sm:w-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Löschen
                </Button>
              )}
              {selectedShift?.series_id && onDeleteSeries && (
                <Button
                  variant="outline"
                  onClick={() => onDeleteSeries(selectedShift.series_id)}
                  disabled={saving}
                  className="w-full sm:w-auto text-destructive border-destructive/40 hover:bg-destructive/5"
                  data-testid="delete-series-btn"
                >
                  <Repeat className="h-4 w-4 mr-2" />
                  Ganze Serie löschen
                </Button>
              )}
              {selectedShift?.series_id && onReassignSeries && (
                <Button
                  variant="outline"
                  onClick={() => onReassignSeries(selectedShift)}
                  disabled={saving}
                  data-testid="reassign-series-btn"
                  className="w-full sm:w-auto"
                >
                  <UserCog className="h-4 w-4 mr-2" />
                  Serie umverteilen
                </Button>
              )}
            </div>
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:ml-auto">
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="w-full sm:w-auto"
              >
                Abbrechen
              </Button>
              <Button
                onClick={onSave}
                disabled={saving || !formData.massnahme_id || !(isHonorar ? formData.honorarkraft_id : formData.employee_id) || (multiDayEnabled && multiDayDays.length === 0)}
                className="w-full sm:w-auto"
                data-testid="shift-save-btn"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {multiDayEnabled
                  ? `${multiDayDays.length} Einsätze erstellen`
                  : 'Speichern'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftDialog;
